/**
* SFE Dashboard API Layer
* Filter cascade order: fiscal_year → yearmonth → division → state → brand → bdm → zm
* Note: Category, Zone, DSH filters are excluded as per requirements.
* All filters support comma-separated multi-select values.
*
* Uses the shared apiClient (utils/axiosConfig) so the Databricks Bearer token
* is attached automatically for every `sfe-dashboard/*` request.
*
* Envelope handling (Req 9.5, 9.6): every response goes through `unwrapEnvelope`,
* which returns `data` for a successful `{ success, data, message }` body, returns the
* body untouched for endpoints that do not use the wrapper yet, and *throws* an
* `SfeApiError` carrying the backend `message` when `success === false`. The previous
* `unwrapResponse` returned `undefined` in that last case, which the panels rendered
* as an empty table with no explanation.
*/
import apiClient from "../../../utils/axiosConfig";
import { getApiUrl } from "../../../Configurations/config";
import {
  ERROR_KIND,
  GENERIC_MESSAGES,
  normalizeApiError,
  readAllowListedMessage,
  readReferenceId,
} from "../../../utils/apiErrors";

// ─── errors ───────────────────────────────────────────────────────────────────

/**
* A failed SFE request, whether the failure arrived as an HTTP error or as a
* `success: false` envelope on a 200 response.
*
* `message` is always a non-empty, display-safe string (allow-listed backend text or a
* generic fallback) and `referenceId` is the support reference when the backend sent
* one (Req 10.3, 10.4). `apiError` mirrors the `ApiErrorDescriptor` the shared Axios
* pipeline attaches to its rejections, so a caller can pass either kind of error
* straight to `formatErrorMessage` without special-casing this class.
*/
export class SfeApiError extends Error {
  constructor(message, { referenceId = null, kind = ERROR_KIND.UNKNOWN, status = null, cause } = {}) {
    // The message is a display string, so it is never allowed to be empty: an empty
    // notification is as unhelpful as the blank panel this class exists to replace.
    const safeMessage = readString(message) ?? FALLBACK_FAILURE_MESSAGE;
    super(safeMessage);
    this.name = "SfeApiError";
    this.referenceId = readString(referenceId) ?? (typeof referenceId === "number" && Number.isFinite(referenceId) ? String(referenceId) : null);
    this.kind = kind;
    this.status = status;
    if (cause !== undefined) this.cause = cause;
    this.apiError = {
      kind,
      status,
      message: safeMessage,
      referenceId: this.referenceId,
      retryAfterMs: null,
      raw: cause === undefined ? this : cause,
    };
  }
}

/** Message used when a failed envelope carries no usable text of its own. */
const FALLBACK_FAILURE_MESSAGE = GENERIC_MESSAGES[ERROR_KIND.UNKNOWN];

/** Trimmed non-empty string, or null. */
function readString(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Plain-ish object test: excludes null and arrays. */
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
* True for a rejection caused by cancellation — either our own `AbortController` or
* Axios' `CanceledError`.
*
* Aborting a superseded request is intentional, so these are rethrown untouched
* rather than converted into a displayable `SfeApiError`.
*/
function isAbortError(error) {
  if (!isRecord(error)) return false;
  return (
    error.name === "AbortError" ||
    error.name === "CanceledError" ||
    error.code === "ERR_CANCELED"
  );
}

/**
* The response body, accepting either an Axios response or the body itself.
*
* An envelope always carries `success`, so it is never mistaken for a response
* wrapper even though both have a `data` key.
*/
function readBody(response) {
  if (!isRecord(response)) return response;
  if ("success" in response) return response;
  if ("data" in response) return response.data;
  return response;
}

/**
* Unwraps the `{ success, data, message }` envelope (Req 9.5, 9.6).
*
* - `success === false` → throws `SfeApiError` with the backend `message`, falling back
*   to a generic string when it is missing, empty, or not a string.
* - `success` present and not false → returns `data`.
* - `success` absent → returns the body unchanged, for endpoints not yet migrated.
*
* The message and reference are read through `utils/apiErrors`, which keeps the
* allow-list and the raw-detail suppression identical to the rest of the app
* (Req 10.2).
*
* @param {unknown} response an Axios response, or the body on its own
* @returns {unknown}
* @throws {SfeApiError} when the envelope reports failure
*/
export function unwrapEnvelope(response) {
  const body = readBody(response);

  if (!isRecord(body) || !("success" in body)) return body;

  if (body.success === false) {
    const asError = { response: { data: body } };
    throw new SfeApiError(readAllowListedMessage(asError) ?? FALLBACK_FAILURE_MESSAGE, {
      referenceId: readReferenceId(asError),
    });
  }

  return body.data;
}

/**
* Normalizes any rejection into the type the callers are documented to see.
*
* Aborts pass through as-is; an existing `SfeApiError` is returned untouched;
* everything else is wrapped around the pipeline's `error.apiError` descriptor when it
* exists, so the sanitized message, kind, status, and reference ID survive.
*/
function asSfeApiError(error) {
  if (isAbortError(error) || error instanceof SfeApiError) return error;

  const descriptor = isRecord(error) && isRecord(error.apiError) ? error.apiError : normalizeApiError(error);

  return new SfeApiError(descriptor.message ?? FALLBACK_FAILURE_MESSAGE, {
    referenceId: descriptor.referenceId ?? null,
    kind: descriptor.kind ?? ERROR_KIND.UNKNOWN,
    status: descriptor.status ?? null,
    cause: error,
  });
}

// ─── helpers ──────────────────────────────────────────────────────────────────

/** In-memory cache with 5-minute TTL */
const _cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function _getCacheKey(url, params) {
  return `${url}|${JSON.stringify(params || {})}`;
}

/**
* One GET, unwrapped, with every failure surfaced as `SfeApiError`.
*
* `signal` is forwarded to Axios so `hooks/useSfeFilters.js` and the panels can cancel
* a superseded request (Req 8.9).
*/
async function envelopeGet(url, { params, signal } = {}) {
  try {
    const response = await apiClient.get(getApiUrl(url), { params, signal });
    return unwrapEnvelope(response);
  } catch (error) {
    throw asSfeApiError(error);
  }
}

/**
* Cached GET. Only successful envelopes are stored: a `success: false` body or an HTTP
* failure throws out of `envelopeGet` before the write, so a transient error cannot be
* replayed for the next five minutes.
*/
async function cachedGet(url, params = {}, { signal } = {}) {
  const key = _getCacheKey(url, params);
  const cached = _cache.get(key);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.data;
  }
  const data = await envelopeGet(url, { params, signal });
  _cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL });
  return data;
}

/** Join array of selected values into comma-separated param for API */
function joinParam(values) {
  if (!values || values.length === 0) return "";
  if (Array.isArray(values)) return values.join(",");
  return String(values);
}

/** `signal` accepted either inside the params object or as a trailing option. */
function readSignal(inlineSignal, options) {
  if (isRecord(options) && options.signal !== undefined) return options.signal;
  return inlineSignal;
}

// ─── Filter cascade endpoints ─────────────────────────────────────────────────
//
// Every cascade fetcher takes a trailing `{ signal }` option and *rethrows*.
//
// `hooks/useSfeFilters.js` owns one `AbortController` per level and aborts the
// previous request before starting a new one (Req 8.9), so the signal has to reach
// `apiClient`. The option is trailing and optional, which keeps the existing
// positional call sites working.
//
// The previous `catch → return []` made a failed level indistinguishable from a
// genuinely empty one, so the hook could never show a per-level error. These
// fetchers therefore let the rejection through as an `SfeApiError`, whose `apiError`
// descriptor the hook formats with `formatErrorMessage`.

export async function fetchFiscalYears({ signal } = {}) {
  const data = await envelopeGet("sfe-dashboard/fiscal-years", { signal });
  return Array.isArray(data) ? data : [];
}

export async function fetchMonths(fiscalYear, { signal } = {}) {
  if (!fiscalYear) return [];
  const data = await envelopeGet("sfe-dashboard/months", {
    params: { fiscal_year: joinParam(fiscalYear) },
    signal,
  });
  return Array.isArray(data) ? data : [];
}

export async function fetchDivisions(fiscalYear, yearmonth, { signal } = {}) {
  if (!fiscalYear || !yearmonth) return [];
  const data = await envelopeGet("sfe-dashboard/divisions", {
    params: {
      fiscal_year: joinParam(fiscalYear),
      yearmonth: joinParam(yearmonth),
    },
    signal,
  });
  return Array.isArray(data) ? data : [];
}

export async function fetchStates(fiscalYear, yearmonth, division, { signal } = {}) {
  if (!fiscalYear || !yearmonth || !division) return [];
  const data = await envelopeGet("sfe-dashboard/states", {
    params: {
      fiscal_year: joinParam(fiscalYear),
      yearmonth: joinParam(yearmonth),
      division: joinParam(division),
    },
    signal,
  });
  return Array.isArray(data) ? data : [];
}

export async function fetchBrands(fiscalYear, yearmonth, division, state, { signal } = {}) {
  if (!fiscalYear || !yearmonth || !division || !state) return [];
  const data = await envelopeGet("sfe-dashboard/brands", {
    params: {
      fiscal_year: joinParam(fiscalYear),
      yearmonth: joinParam(yearmonth),
      division: joinParam(division),
      state: joinParam(state),
    },
    signal,
  });
  return Array.isArray(data) ? data : [];
}

export async function fetchBdmList(yearmonth, division, state = [], brand = [], { signal } = {}) {
  if (!yearmonth || !division) return [];
  const data = await envelopeGet("sfe-dashboard/bdm-list", {
    params: {
      yearmonth: joinParam(yearmonth),
      division: joinParam(division),
      state: joinParam(state),
      brand: joinParam(brand),
    },
    signal,
  });
  return Array.isArray(data) ? data : [];
}

export async function fetchZmList(
  yearmonth,
  division,
  state = [],
  brand = [],
  bdm = [],
  { signal } = {},
) {
  if (!yearmonth || !division) return [];
  const data = await envelopeGet("sfe-dashboard/zm-list", {
    params: {
      yearmonth: joinParam(yearmonth),
      division: joinParam(division),
      state: joinParam(state),
      brand: joinParam(brand),
      bdm: joinParam(bdm),
    },
    signal,
  });
  return Array.isArray(data) ? data : [];
}

// ─── Dashboard data endpoints ─────────────────────────────────────────────────
//
// These used to `catch → return` a default shape (`null`, `[]`, an empty totals
// object), which is exactly why a `success: false` envelope rendered as a blank panel:
// the failure never left this module. They now throw `SfeApiError`, and the dashboard
// holds per-panel error state (task 11.4).

export async function fetchDashboardSummary(
  {
    yearmonth,
    fiscal_year,
    division,
    state = [],
    brand = [],
    bdm = [],
    zm = [],
    signal,
  } = {},
  options = {},
) {
  return envelopeGet("sfe-dashboard/summary", {
    params: {
      yearmonth: joinParam(yearmonth),
      fiscal_year: joinParam(fiscal_year),
      division: joinParam(division),
      state: joinParam(state),
      brand: joinParam(brand),
      bdm: joinParam(bdm),
      zm: joinParam(zm),
    },
    signal: readSignal(signal, options),
  });
}

export async function fetchHqPerformance(
  {
    yearmonth,
    fiscal_year,
    division,
    state = [],
    brand = [],
    bdm = [],
    zm = [],
    view_by = "HQ",
    signal,
  } = {},
  options = {},
) {
  return cachedGet(
    "sfe-dashboard/hq-performance",
    {
      yearmonth: joinParam(yearmonth),
      fiscal_year: joinParam(fiscal_year),
      division: joinParam(division),
      state: joinParam(state),
      brand: joinParam(brand),
      bdm: joinParam(bdm),
      zm: joinParam(zm),
      view_by,
    },
    { signal: readSignal(signal, options) },
  );
}

export async function fetchTmPerformance(
  {
    yearmonth,
    fiscal_year,
    division,
    state = [],
    brand = [],
    bdm = [],
    zm = [],
    signal,
  } = {},
  options = {},
) {
  const data = await cachedGet(
    "sfe-dashboard/tm-performance",
    {
      yearmonth: joinParam(yearmonth),
      fiscal_year: joinParam(fiscal_year),
      division: joinParam(division),
      state: joinParam(state),
      brand: joinParam(brand),
      bdm: joinParam(bdm),
      zm: joinParam(zm),
    },
    { signal: readSignal(signal, options) },
  );
  return Array.isArray(data) ? data : [];
}

export async function fetchBrandTable(
  {
    yearmonth,
    fiscal_year,
    division,
    state = [],
    brand = [],
    bdm = [],
    zm = [],
    page = 1,
    page_size = 20,
    signal,
  } = {},
  options = {},
) {
  return envelopeGet("sfe-dashboard/brand-table", {
    params: {
      yearmonth: joinParam(yearmonth),
      fiscal_year: joinParam(fiscal_year),
      division: joinParam(division),
      state: joinParam(state),
      brand: joinParam(brand),
      bdm: joinParam(bdm),
      zm: joinParam(zm),
      page,
      page_size,
    },
    signal: readSignal(signal, options),
  });
}

export async function fetchVolatility(
  { yearmonth, division, state = [], brand = [], signal } = {},
  options = {},
) {
  return cachedGet(
    "sfe-dashboard/volatility",
    {
      yearmonth: joinParam(yearmonth),
      division: joinParam(division),
      state: joinParam(state),
      brand: joinParam(brand),
    },
    { signal: readSignal(signal, options) },
  );
}

export async function fetchCreditNotes(
  {
    yearmonth,
    fiscal_year,
    division,
    state = [],
    brand = [],
    cn_type = "Expiry",
    signal,
  } = {},
  options = {},
) {
  return cachedGet(
    "sfe-dashboard/credit-notes",
    {
      yearmonth: joinParam(yearmonth),
      fiscal_year: joinParam(fiscal_year),
      division: joinParam(division),
      state: joinParam(state),
      brand: joinParam(brand),
      cn_type,
    },
    { signal: readSignal(signal, options) },
  );
}

export async function fetchBreakdownTable(
  {
    yearmonth,
    fiscal_year,
    division,
    group_by = "brand",
    state = [],
    brand = [],
    bdm = [],
    zm = [],
    signal,
  } = {},
  options = {},
) {
  const data = await cachedGet(
    "sfe-dashboard/breakdown-table",
    {
      yearmonth: joinParam(yearmonth),
      fiscal_year: joinParam(fiscal_year),
      division: joinParam(division),
      group_by,
      state: joinParam(state),
      brand: joinParam(brand),
      bdm: joinParam(bdm),
      zm: joinParam(zm),
    },
    { signal: readSignal(signal, options) },
  );

  // Backend now returns { data: [...], page, page_size, total }; the flat-array shape
  // is still accepted. An empty array here means "no rows", never "the request failed".
  if (data && Array.isArray(data.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
}

export default apiClient;
 